import { guessColumnMap, parseCSVTable } from "../../../utils/fileUtils";
import { CountryList, REGEX_PHONE } from "../../../utils/constants";
// The shared postal-field key, so "the same address" means the same thing here
// and in isAddressEqual. Phone is outside the key on purpose: the same yard
// listed twice with and without a dispatch number is one address.
import { addressKey } from "../../../utils/utils";

/**
 * The spreadsheet-to-address mapping, and the checks a mapped row has to pass
 * before it becomes an address-book entry.
 *
 * A saved address is seven fields wide and every one of them is typed by hand
 * today, so the import's real work is normalising what a spreadsheet spells
 * loosely — "Texas", "United States", a zip that lost its leading zero to Excel
 * — into the exact values the address form would have produced, and refusing
 * the rows it cannot. Kept pure and free of AppSync so the row decisions can be
 * tested without a network.
 */

// Column mapping labels. Everything but Country and Phone is required: an
// address book entry prints on a BOL, and the create dialog already refuses a
// partial address, so an import must not be the one way to get one in.
// Country is optional because most files simply do not carry the column — an
// unmapped or blank Country is taken as USA (COUNTRY_DEFAULT below).
export const ADDRESS_IMPORT_FIELDS = [
  { id: "name", name: "Business Name", required: true },
  { id: "street", name: "Street Address", required: true },
  { id: "city", name: "City", required: true },
  { id: "state", name: "State", required: true },
  { id: "zipCode", name: "Zip Code", required: true },
  { id: "country", name: "Country" },
  { id: "phone", name: "Phone" },
];

export const REQUIRED_ADDRESS_IMPORT_FIELDS = ADDRESS_IMPORT_FIELDS.filter(
  (field) => field.required,
);

export const COUNTRY_DEFAULT = "USA";

// Zip codes are stored as text, and the address form requires at least five
// characters — enough for a US ZIP and for a Canadian postal code, which is six
// with the space dropped.
const MIN_ZIP_LENGTH = 5;

/**
 * Turn an address CSV file's text into header-keyed rows.
 *
 * @param {string} text - the file contents
 * @returns {{columns: {id: string, name: string}[], rows: object[], errors: string[]}}
 */
export const parseAddressCSV = parseCSVTable;

// Country spellings a spreadsheet arrives with, reduced to the CountryList ids
// the address form stores. Anything not listed is refused rather than guessed:
// the address form offers these three countries only, and an entry filed under
// a country the form cannot display is one nobody can edit afterwards.
const COUNTRY_ALIASES = {
  us: "USA",
  usa: "USA",
  unitedstates: "USA",
  unitedstatesofamerica: "USA",
  america: "USA",
  ca: "CA",
  can: "CA",
  canada: "CA",
  mx: "MX",
  mex: "MX",
  mexico: "MX",
};

// Full state and province names, so a file that spells them out maps to the
// two-letter code the address form stores. Codes themselves pass through the
// two-letter check below without needing an entry here.
const STATE_NAMES = {
  alabama: "AL",
  alaska: "AK",
  arizona: "AZ",
  arkansas: "AR",
  california: "CA",
  colorado: "CO",
  connecticut: "CT",
  delaware: "DE",
  districtofcolumbia: "DC",
  florida: "FL",
  georgia: "GA",
  hawaii: "HI",
  idaho: "ID",
  illinois: "IL",
  indiana: "IN",
  iowa: "IA",
  kansas: "KS",
  kentucky: "KY",
  louisiana: "LA",
  maine: "ME",
  maryland: "MD",
  massachusetts: "MA",
  michigan: "MI",
  minnesota: "MN",
  mississippi: "MS",
  missouri: "MO",
  montana: "MT",
  nebraska: "NE",
  nevada: "NV",
  newhampshire: "NH",
  newjersey: "NJ",
  newmexico: "NM",
  newyork: "NY",
  northcarolina: "NC",
  northdakota: "ND",
  ohio: "OH",
  oklahoma: "OK",
  oregon: "OR",
  pennsylvania: "PA",
  puertorico: "PR",
  rhodeisland: "RI",
  southcarolina: "SC",
  southdakota: "SD",
  tennessee: "TN",
  texas: "TX",
  utah: "UT",
  vermont: "VT",
  virginia: "VA",
  washington: "WA",
  westvirginia: "WV",
  wisconsin: "WI",
  wyoming: "WY",
  alberta: "AB",
  britishcolumbia: "BC",
  manitoba: "MB",
  newbrunswick: "NB",
  newfoundlandandlabrador: "NL",
  novascotia: "NS",
  ontario: "ON",
  princeedwardisland: "PE",
  quebec: "QC",
  saskatchewan: "SK",
};

const letters = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");

const cellValue = (row, column) =>
  column ? String(row?.[column] ?? "").trim() : "";

/**
 * The address a mapped row describes, in the shape SavedAddress stores.
 *
 * Normalised here rather than at write time so the plan, the preview grid and
 * the created record all agree on exactly what will be saved — the manager
 * reviews the value that gets written, not the value the file spelled.
 *
 * @param {object} row - a parseAddressCSV row
 * @param {object} fieldMap - {[fieldId]: csvColumn}
 * @returns {{name: string, street: string, city: string, state: string,
 *   zipCode: string, country: string, phone: string}}
 */
export const mapAddressRow = (row, fieldMap) => {
  const rawState = cellValue(row, fieldMap?.state);
  const rawCountry = cellValue(row, fieldMap?.country);

  return {
    name: cellValue(row, fieldMap?.name),
    street: cellValue(row, fieldMap?.street),
    city: cellValue(row, fieldMap?.city),
    state: STATE_NAMES[letters(rawState)] ?? rawState.toUpperCase(),
    // Uppercased for Canadian postal codes, which the form stores that way.
    zipCode: cellValue(row, fieldMap?.zipCode).toUpperCase(),
    // A blank country is the common case, not an error — see COUNTRY_DEFAULT.
    // An unrecognised one is left as written so the row can be skipped by name.
    country: rawCountry
      ? (COUNTRY_ALIASES[letters(rawCountry)] ?? rawCountry)
      : COUNTRY_DEFAULT,
    phone: cellValue(row, fieldMap?.phone),
  };
};

/**
 * Decide every row: which addresses to create, and why the rest are being left.
 *
 * A row is skipped rather than fixed. Guessing at a blank city or filing a row
 * under a country the address form cannot show would write entries the manager
 * never reviewed, and these entries print on bills of lading; naming the row and
 * its reason lets them correct the spreadsheet and re-upload, which is the loop
 * the dialog is built around.
 *
 * Rows are checked against the addresses already accepted from this same file as
 * well as the account's book, so a file that lists one address twice imports it
 * once — and says which of the two it collided with, since the fix is in a
 * different place for each.
 *
 * @param {object[]} rows - parseAddressCSV rows
 * @param {object} fieldMap - {[fieldId]: csvColumn}
 * @param {object[]} existingAddresses - the account's saved addresses
 * @returns {{decisions: {rowIndex: number, label: string, values: object, reason: string|null}[],
 *   importable: object[], skipped: object[]}} one decision per row, plus the two
 *   views of it the dialog and the import loop each want.
 */
export const planAddressImport = (rows, fieldMap, existingAddresses) => {
  const book = new Set(
    (existingAddresses ?? []).map((address) => addressKey(address)),
  );
  // Rows accepted from this file are kept apart from the account's book so the
  // second copy of an address in the file collides with the first and is named
  // for it. Folding them together would send the manager to the address book
  // to look for an entry that is not there yet.
  const acceptedFromFile = new Set();

  const decide = (values) => {
    const missing = REQUIRED_ADDRESS_IMPORT_FIELDS.find(
      (field) => !values[field.id],
    );
    if (missing) return `missing "${missing.name}"`;
    // A mis-mapped column lands a city name in State. Skipping the row names the
    // file's problem; importing it would print a bill of lading addressed to a
    // state that does not exist.
    if (!/^[A-Z]{2}$/.test(values.state))
      return `"${values.state}" is not a 2-letter state`;
    if (values.zipCode.length < MIN_ZIP_LENGTH)
      return `"${values.zipCode}" is not a valid zip code`;
    if (!CountryList.some((country) => country.id === values.country))
      return `"${values.country}" is not a supported country`;
    if (values.phone && !REGEX_PHONE.test(values.phone))
      return `"${values.phone}" is not a phone number`;
    const key = addressKey(values);
    if (book.has(key)) return "already in the address book";
    if (acceptedFromFile.has(key)) return "listed earlier in this file";
    return null;
  };

  const decisions = (rows ?? []).map((row, index) => {
    const values = mapAddressRow(row, fieldMap);
    const reason = decide(values);
    if (!reason) acceptedFromFile.add(addressKey(values));
    return {
      rowIndex: row?.rowIndex ?? index,
      label: `Row ${index + 1}${values.name ? ` (${values.name})` : ""}`,
      values,
      reason,
    };
  });

  return {
    decisions,
    importable: decisions.filter((d) => !d.reason).map((d) => d.values),
    skipped: decisions.filter((d) => d.reason),
  };
};

// Header spellings seen in the exports yards actually arrive with.
const HEADER_SYNONYMS = {
  name: [
    "businessname",
    "name",
    "company",
    "companyname",
    "customer",
    "customername",
    "consignee",
    "shipto",
    "shiptoname",
    "location",
    "locationname",
  ],
  street: [
    "street",
    "streetaddress",
    "address",
    "addressline",
    "shiptoaddress",
  ],
  city: ["city", "town", "shiptocity"],
  state: ["state", "province", "stateprovince", "region", "shiptostate"],
  zipCode: ["zip", "zipcode", "postalcode", "postcode", "postal", "shiptozip"],
  country: ["country", "countrycode", "nation"],
  phone: ["phone", "phonenumber", "telephone", "tel", "mobile", "contactphone"],
};

/**
 * A starting mapping, so a well-named export needs no dropdown work.
 *
 * @param {{id: string}[]} columns - parseAddressCSV columns
 * @returns {object} {[fieldId]: csvColumn}
 */
export const guessFieldMap = (columns) =>
  guessColumnMap(ADDRESS_IMPORT_FIELDS, HEADER_SYNONYMS, columns);

/**
 * Whether the mapping is complete enough to import at all.
 *
 * @param {object} fieldMap - {[fieldId]: csvColumn}
 * @returns {string[]} one message per unmapped required field
 */
export const missingRequiredMappings = (fieldMap) =>
  REQUIRED_ADDRESS_IMPORT_FIELDS.filter((field) => !fieldMap?.[field.id]).map(
    (field) => `"${field.name}" mapping is required.`,
  );

/**
 * One notification for a finished import.
 *
 * @param {{created: number, skipped: number, failed: number}} summary
 * @returns {string}
 */
export const describeImportSummary = ({ created, skipped, failed }) => {
  const parts = [`${created} address${created === 1 ? "" : "es"} imported`];
  if (skipped) parts.push(`${skipped} skipped`);
  if (failed) parts.push(`${failed} failed`);
  return `${parts.join(", ")}.`;
};
